import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  updateLeadStatus,
  assignLeadToUser,
  deleteLead,
  getLeadAssignedUsers,
  getLeadActivityLogs,
  getLeadAttachments,
  downloadLeadFile,
  createCustomerFromLead,
  createShareableLink,
  cancelLeadAttachment,
  regenerateLeadCredentials,
} from '../../../store/slices/leadsSlice';
import { createIssue } from '../../../store/slices/issueSlice';
import SweetAlert from '../../../utils/sweetAlert';
import { findHoldIuwStatus, findResolvedIuwStatus, isQcOrQcManagerRole, isQcApprovedOrDeclinedStatus } from '../utils';
import { fetchLeadAssignUserOptions, normalizeAssignUserOptions, shouldPrefillLeadAssignees } from '../leadAssignUserHelpers';
import { useAuth } from '../../../hooks/useAuth';

/** `datetime-local` value from API datetime string */
function toDatetimeLocalValue(value) {
  if (value == null || value === '') return '';
  const d = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getCurrentDatetimeLocalValue() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/** Match API lead shape: current status id lives on last_status_log, not always lead.status.id */
function resolveLeadStatusId(lead) {
  if (!lead) return '';
  const raw =
    lead.last_status_log?.status?.id ??
    lead.status?.status?.id ??
    lead.status?.id ??
    lead.status_id;
  if (raw === undefined || raw === null || raw === '') return '';
  return raw;
}

/** Align with statusOptions so Autocomplete finds the option (string vs number id) */
function matchStatusId(statusOptions, rawId) {
  if (rawId === '' || rawId == null) return '';
  const list = statusOptions || [];
  const found = list.find(
    (s) => s.id === rawId || String(s.id) === String(rawId)
  );
  return found ? found.id : rawId;
}

export const useLeadActions = (leadsData, permissions = {}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const {
    canChangeLeadStatus = true,
    canAssignLeads = true,
    canDeleteLeads = true,
    canCreateShareableLink = true,
  } = permissions;

  const [dialogs, setDialogs] = useState({
    status: false,
    assign: false,
    share: false,
    attachments: false,
    assignedUsers: false,
    activityLogs: false,
    issue: false,
    createCustomer: false,
    initialDeal: false,
    cancelAttachment: false,
    addAttachment: false,
  });
  const [cancelAttachmentTarget, setCancelAttachmentTarget] = useState(null);
  const [addAttachmentTarget, setAddAttachmentTarget] = useState(null);
  const [statusData, setStatusData] = useState({
    newStatus: '',
    conditionalData: {
      date: '',
      description: '',
      // assignee_id: '',
      assignees: [],
      equipment_model_id: '',
      lease_id: '',
      merchant_service_provider_id: '',
      timeZone: 'UTC',
      startDateTime: '',
      endDateTime: '',
      meetingDescription: '',
    },
  });
  const [assignData, setAssignData] = useState({ selectedUsers: [] });
  const [assignUserOptions, setAssignUserOptions] = useState([]);
  const [assignUserOptionsLoading, setAssignUserOptionsLoading] = useState(false);
  const [shareData, setShareData] = useState({ url: '', password: '' });
  const [shareLoading, setShareLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignedUsersLoading, setAssignedUsersLoading] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [createdCustomer, setCreatedCustomer] = useState(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [credentialsDialogMeta, setCredentialsDialogMeta] = useState({
    title: 'Customer Created Successfully',
    description: 'Please save these credentials for the new customer:',
    note: 'These credentials have been automatically saved. The customer can use them to log into their account.',
  });
  const [selectedLead, setSelectedLead] = useState(null);

  const handleViewLead = (id) => navigate(`/leads/view/${id}`);
  const handleEditLead = (id) => navigate(`/leads/edit/${id}`);
  const handleCreateLead = () => navigate('/leads/create');
  const handleCreateDba = (leadId) => navigate(`/create-merchant?lead_id=${leadId}`);
  const handleEditDba = (leadId) => navigate(`/create-merchant?edit=true&id=${leadId}`);

  const openDialog = (name) => setDialogs((p) => ({ ...p, [name]: true }));
  const closeDialog = (name) => setDialogs((p) => ({ ...p, [name]: false }));

  const handleOpenIssueDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('issue');
  };

  const handleCloseIssueDialog = () => closeDialog('issue');

  const handleOpenCreateCustomerDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('createCustomer');
  };

  const handleCloseCreateCustomerDialog = () => closeDialog('createCustomer');

  const handleOpenInitialDealDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('initialDeal');
  };

  const handleCloseInitialDealDialog = () => closeDialog('initialDeal');

  const handleOpenAddAttachmentDialog = (lead) => {
    setSelectedLead(lead);
    setAddAttachmentTarget(lead);
    openDialog('addAttachment');
  };

  const handleCloseAddAttachmentDialog = () => {
    closeDialog('addAttachment');
    setAddAttachmentTarget(null);
  };

  const handleOpenCancelAttachmentDialog = (lead, attachment) => {
    setSelectedLead(lead);
    setCancelAttachmentTarget(attachment);
    openDialog('cancelAttachment');
  };

  const handleCloseCancelAttachmentDialog = () => {
    closeDialog('cancelAttachment');
    setCancelAttachmentTarget(null);
  };

  const handleCancelAttachment = async ({ status, comment }) => {
    const leadId = selectedLead?.id;
    const attachmentId =
      cancelAttachmentTarget?.id ??
      cancelAttachmentTarget?.attachment_id ??
      cancelAttachmentTarget?.uuid;

    if (!leadId || !attachmentId) {
      toast.error('Missing lead or attachment');
      return;
    }

    try {
      leadsData.setProcessingId(leadId);
      const res = await dispatch(
        cancelLeadAttachment({ leadId, attachmentId, status, comment })
      ).unwrap();

      if (res?.success !== false) {
        const fallbackMessage =
          status === 2 ? 'Attachment resolved successfully' : 'Attachment rejected successfully';
        toast.success(res?.message || fallbackMessage);
        handleCloseCancelAttachmentDialog();
        leadsData.fetchLeads?.();
      } else {
        toast.error(res?.message || 'Failed to update attachment status');
      }
    } catch (err) {
      toast.error(err?.message || err || 'Failed to update attachment status');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleOpenHoldIuwStatusDialog = async (lead) => {
    if (!canChangeLeadStatus) {
      toast.error('You do not have permission to change lead status.');
      return;
    }

    const statuses =
      (await leadsData.ensureStatusFilterOptions?.()) || leadsData.statusOptions || [];
    const holdIuwStatus = findHoldIuwStatus(statuses);
    if (!holdIuwStatus) {
      toast.error('Hold IUW status is not configured');
      return;
    }

    setSelectedLead(lead);
    setStatusData({
      newStatus: holdIuwStatus.id,
      conditionalData: {
        date: getCurrentDatetimeLocalValue(),
        description: '',
        assignees: lead?.assignees?.map((a) => a.id ?? a.uuid).filter(Boolean) ?? [],
        equipment_model_id: '',
        lease_id: '',
        merchant_service_provider_id: '',
        timeZone: 'UTC',
        startDateTime: '',
        endDateTime: '',
        meetingDescription: '',
      },
    });
    openDialog('status');
  };

  const handleOpenResolvedIuwStatusDialog = async (lead) => {
    if (!canChangeLeadStatus) {
      toast.error('You do not have permission to change lead status.');
      return;
    }

    const statuses =
      (await leadsData.ensureStatusFilterOptions?.()) || leadsData.statusOptions || [];
    const resolvedIuwStatus = findResolvedIuwStatus(statuses);
    if (!resolvedIuwStatus) {
      toast.error('Resolved IUW status is not configured');
      return;
    }

    setSelectedLead(lead);
    setStatusData({
      newStatus: resolvedIuwStatus.id,
      conditionalData: {
        date: getCurrentDatetimeLocalValue(),
        description: '',
        assignees: lead?.assignees?.map((a) => a.id ?? a.uuid).filter(Boolean) ?? [],
        equipment_model_id: '',
        lease_id: '',
        merchant_service_provider_id: '',
        timeZone: 'UTC',
        startDateTime: '',
        endDateTime: '',
        meetingDescription: '',
      },
    });
    openDialog('status');
  };

  const handleCloseCredentialsDialog = () => {
    setShowCredentialsDialog(false);
    setCreatedCustomer(null);
    setCredentialsDialogMeta({
      title: 'Customer Created Successfully',
      description: 'Please save these credentials for the new customer:',
      note: 'These credentials have been automatically saved. The customer can use them to log into their account.',
    });
  };

  const handleRegenerateCustomerCredentials = async (lead) => {
    const leadId = lead?.id ?? lead?.uuid;
    if (!leadId) {
      toast.error('No lead selected');
      return;
    }

    try {
      leadsData.setProcessingId(leadId);
      const res = await dispatch(regenerateLeadCredentials(leadId)).unwrap();
      const payload = res?.data ?? res;

      setCreatedCustomer({
        portal_url: payload?.portal_url ?? '',
        email: payload?.email ?? '',
        password: payload?.password ?? '',
      });
      setCredentialsDialogMeta({
        title: 'Customer Credentials',
        description: 'Save these credentials for the customer portal:',
        note: 'Share these details securely with the customer.',
      });
      setShowCredentialsDialog(true);
      toast.success(res?.message || 'Customer credentials generated successfully');
    } catch (err) {
      toast.error(typeof err === 'string' ? err : err?.message || 'Failed to generate customer credentials');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleCreateCustomer = async (formData) => {
    try {
      if (!selectedLead) {
        toast.error('No lead selected');
        return;
      }
      leadsData.setProcessingId(selectedLead.id);
      const payload = { ...formData, lead_id: selectedLead.id };
      const res = await dispatch(createCustomerFromLead(payload)).unwrap();

      if (res?.success) {
        const credentials = {
          email: formData.email,
          password: formData.password,
          name: formData.name || selectedLead.name,
        };
        setCredentialsDialogMeta({
          title: 'Customer Created Successfully',
          description: 'Please save these credentials for the new customer:',
          note: 'These credentials have been automatically saved. The customer can use them to log into their account.',
        });
        setCreatedCustomer(credentials);
        handleCloseCreateCustomerDialog();
        setShowCredentialsDialog(true);
        toast.success('Customer created successfully!');
      } else {
        toast.error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to create customer');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleCreateIssue = async (issueData) => {
    try {
      leadsData.setProcessingId(selectedLead.id);
      const res = await dispatch(createIssue(issueData)).unwrap();
      if (res?.success) {
        toast.success('Issue created successfully');
        handleCloseIssueDialog();
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to create issue');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleOpenStatusDialog = async (lead) => {
    const statuses =
      (await leadsData.ensureStatusFilterOptions?.()) || leadsData.statusOptions || [];
    setSelectedLead(lead);
    const rawStatusId = resolveLeadStatusId(lead);
    const newStatus = matchStatusId(statuses, rawStatusId);

    const log = lead?.last_status_log;
    const meeting = log?.meeting;
    const statusName =
      log?.status?.name || lead?.status?.status?.name || lead?.status?.name;

    const assigneeIdsFromLead =
      lead?.assignees?.map((a) => a.id ?? a.uuid).filter(Boolean) ?? [];

    const currentDateTime = getCurrentDatetimeLocalValue();

    const conditionalData = {
      date: currentDateTime,
      description: '',
      assignees: [],
      equipment_model_id:
        lead?.equipment_model_id ?? lead?.equipment_model?.id ?? '',
      lease_id: lead?.lease_id ?? lead?.lease?.id ?? '',
      merchant_service_provider_id:
        lead?.merchant_service_provider_id ??
        lead?.merchant_service_provider?.id ??
        '',
      timeZone: 'UTC',
      startDateTime: '',
      endDateTime: '',
      meetingDescription: '',
    };

    if (statusName === 'Meeting' && meeting) {
      conditionalData.assignees = assigneeIdsFromLead;
      conditionalData.timeZone = meeting.time_zone || 'UTC';
      conditionalData.startDateTime = toDatetimeLocalValue(meeting.start_date_time);
      conditionalData.endDateTime = toDatetimeLocalValue(meeting.end_date_time);
      conditionalData.meetingDescription = meeting.description || '';
    }

    setStatusData({
      newStatus,
      conditionalData,
    });
    openDialog('status');
  };

  const handleCloseStatusDialog = () => {
    closeDialog('status');
    setStatusData({
      newStatus: '',
      conditionalData: {
        date: '',
        description: '',
        assignees: [],
        equipment_model_id: '',
        lease_id: '',
        merchant_service_provider_id: '',
        timeZone: 'UTC',
        startDateTime: '',
        endDateTime: '',
        meetingDescription: '',
      },
    });
  };

  const handleOpenAssignDialog = (lead) => {
    setSelectedLead(lead);
    const prefillAssignees = shouldPrefillLeadAssignees(user);
    const existingIds = prefillAssignees
      ? (lead?.assignees?.map((a) => a.id ?? a.uuid).filter(Boolean) ?? [])
      : [];
    // Assign dialog is single-select — keep at most one prefilled user
    setAssignData({ selectedUsers: existingIds.slice(0, 1) });
    openDialog('assign');

    setAssignUserOptionsLoading(true);
    fetchLeadAssignUserOptions(dispatch, user, leadsData.usersList)
      .then((options) => {
        if (!prefillAssignees) {
          setAssignUserOptions(options);
          return;
        }
        const existingAssignees = normalizeAssignUserOptions(lead?.assignees ?? []);
        const merged = [...options];
        existingAssignees.forEach((assignee) => {
          if (!merged.some((option) => String(option.id) === String(assignee.id))) {
            merged.push(assignee);
          }
        });
        setAssignUserOptions(merged);
      })
      .catch(() => {
        toast.error('Failed to load assignable users');
        setAssignUserOptions([]);
      })
      .finally(() => setAssignUserOptionsLoading(false));
  };

  const handleCloseAssignDialog = () => {
    closeDialog('assign');
    setAssignData({ selectedUsers: [] });
    setAssignUserOptions([]);
  };

  const handleOpenShareDialog = async (lead) => {
    setSelectedLead(lead);
    openDialog('share');
    await generateShareableLink(lead.id);
  };

  const handleCloseShareDialog = () => {
    closeDialog('share');
    setShareData({ url: '', password: '' });
  };

  const handleOpenAttachmentsDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('attachments');
    fetchAttachments(lead.id);
  };

  const handleCloseAttachmentsDialog = () => {
    closeDialog('attachments');
    setAttachments([]);
  };

  const handleOpenAssignedUsersDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('assignedUsers');
    fetchAssignedUsers(lead.id);
  };

  const handleCloseAssignedUsersDialog = () => {
    closeDialog('assignedUsers');
    setAssignedUsers([]);
  };

  const handleOpenActivityLogsDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('activityLogs');
    fetchActivityLogs(lead.id);
  };

  const handleCloseActivityLogsDialog = () => {
    closeDialog('activityLogs');
    setActivityLogs([]);
  };

  const handleStatusChange = async () => {
    if (!canChangeLeadStatus) {
      toast.error('You do not have permission to change lead status.');
      return;
    }
    if (!statusData.newStatus) {
      toast.warn('Select a status');
      return;
    }

    const statusObj = leadsData.statusOptions.find((s) => s.id === statusData.newStatus);
    const statusName = statusObj?.name;
    const normalizedStatusName = String(statusName || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
    const payload = {
      status_id: statusData.newStatus,
      date: statusData.conditionalData.date,
      description: statusData.conditionalData.description,
    };

    if (statusName === 'Select') {
      if (!statusData.conditionalData.date || !statusData.conditionalData.description) {
        toast.warn('Date & description are required');
        return;
      }
    }

    if (statusName === 'Meeting') {
      if (
        !statusData.conditionalData.assignees?.length ||
        !statusData.conditionalData.startDateTime ||
        !statusData.conditionalData.endDateTime ||
        !statusData.conditionalData.meetingDescription
      ) {
        toast.warn('All meeting fields are required');
        return;
      }
      Object.assign(payload, {
        // assignee_id: statusData.conditionalData.assignee_id,
        assignees: statusData.conditionalData.assignees,
        time_zone: statusData.conditionalData.timeZone,
        start_date_time: statusData.conditionalData.startDateTime,
        end_date_time: statusData.conditionalData.endDateTime,
        description: statusData.conditionalData.meetingDescription,
      });
    }

    if ((statusName || '').toLowerCase().trim() === 'qualified') {
      if (statusData.conditionalData.assignees?.length) {
        payload.assignees = statusData.conditionalData.assignees;
      }
    }

    // QC / QC Manager → QC Approved or QC Declined locks the lead
    if (isQcOrQcManagerRole(user) && isQcApprovedOrDeclinedStatus(statusName)) {
      payload.is_disabled = true;
    }

    if (
      normalizedStatusName.includes('submitted') &&
      normalizedStatusName.includes('equipment')
    ) {
      if (!statusData.conditionalData.equipment_model_id) {
        toast.warn('Please select equipment');
        return;
      }
      payload.equipment_model_id = statusData.conditionalData.equipment_model_id;
    }

    if (
      normalizedStatusName.includes('submitted') &&
      normalizedStatusName.includes('lease')
    ) {
      if (!statusData.conditionalData.lease_id) {
        toast.warn('Please select lease');
        return;
      }
      payload.lease_id = statusData.conditionalData.lease_id;
    }

    if (
      normalizedStatusName.includes('submitted') &&
      (normalizedStatusName.includes('(msp)') ||
        normalizedStatusName.includes('msp'))
    ) {
      if (!statusData.conditionalData.merchant_service_provider_id) {
        toast.warn('Please select merchant service provider');
        return;
      }
      payload.merchant_service_provider_id =
        statusData.conditionalData.merchant_service_provider_id;
    }

    try {
      leadsData.setProcessingId(selectedLead.id);
      const res = await dispatch(
        updateLeadStatus({ leadId: selectedLead.id, payload })
      ).unwrap();
      if (res?.success) {
        leadsData.fetchLeads();
        toast.success('Status updated');
        handleCloseStatusDialog();
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to update status');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleAssign = async () => {
    if (!canAssignLeads) {
      toast.error('You do not have permission to assign leads.');
      return;
    }
    if (!assignData.selectedUsers?.length) {
      toast.warn('Select a user');
      return;
    }
    try {
      leadsData.setProcessingId(selectedLead.id);
      const userIds = assignData.selectedUsers;
      await dispatch(
        assignLeadToUser({ leadId: selectedLead.id, userIds })
      ).unwrap();
      toast.success('Lead assigned');
      handleCloseAssignDialog();
      await leadsData.fetchLeads();
    } catch (err) {
      toast.error(err?.message || 'Failed to assign lead');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleDeleteLead = async (lead) => {
    if (!canDeleteLeads) {
      toast.error('You do not have permission to delete leads.');
      return;
    }
    try {
      const result = await SweetAlert.deleteConfirm(lead.name || 'this lead');
      if (!result.isConfirmed) return;

      leadsData.setProcessingId(lead.id);
      await dispatch(deleteLead(lead.id)).unwrap();
      await SweetAlert.success({ text: 'Lead deleted successfully' });
      leadsData.fetchLeads();
    } catch (error) {
      console.error('Delete lead error:', error);
      await SweetAlert.error({ text: 'Failed to delete lead' });
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const generateShareableLink = async (leadId) => {
    if (!canCreateShareableLink) {
      toast.error('You do not have permission to create shareable links.');
      return;
    }
    if (!leadId) return;
    try {
      setShareLoading(true);
      const res = await dispatch(createShareableLink(leadId)).unwrap();
      const linkData = res?.data ?? res;
      if (linkData?.shareable_url) {
        setShareData({
          url: linkData.shareable_url,
          password: linkData.password || '',
        });
      } else if (res?.data?.data) {
        setShareData({
          url: res.data.data.shareable_url,
          password: res.data.data.password || '',
        });
      } else {
        toast.error(res?.message || 'No link returned');
        handleCloseShareDialog();
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to create shareable link');
      handleCloseShareDialog();
    } finally {
      setShareLoading(false);
    }
  };

  const fetchAttachments = async (leadId) => {
    if (!leadId) return;
    try {
      setAttachmentsLoading(true);
      const res = await dispatch(getLeadAttachments(leadId)).unwrap();
      const list =
        res?.attachments ?? res?.data?.attachments ?? res?.data?.data?.attachments ?? [];
      setAttachments(list);
    } catch (err) {
      toast.error(err?.message || 'Failed to load attachments');
      handleCloseAttachmentsDialog();
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const fetchAssignedUsers = async (leadId) => {
    if (!leadId) return;
    try {
      setAssignedUsersLoading(true);
      const res = await dispatch(getLeadAssignedUsers(leadId)).unwrap();
      const list = res?.data?.data?.status_logs ?? res?.data?.status_logs ?? res?.status_logs ?? [];
      setAssignedUsers(list);
    } catch (err) {
      toast.error(err?.message || 'Failed to load assigned users');
      handleCloseAssignedUsersDialog();
    } finally {
      setAssignedUsersLoading(false);
    }
  };

  const fetchActivityLogs = async (leadId) => {
    if (!leadId) return;
    try {
      setActivityLogsLoading(true);
      const res = await dispatch(getLeadActivityLogs(leadId)).unwrap();
      const list = res?.data ?? res ?? [];
      setActivityLogs(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load activity logs');
      handleCloseActivityLogsDialog();
    } finally {
      setActivityLogsLoading(false);
    }
  };

  const handleDownloadFile = async (file, index) => {
    const filePath = file.path;
    const fileName = file?.title || file?.file_name || file?.name || `File ${index + 1}`;

    if (!filePath) {
      toast.error('No file path');
      return;
    }
    try {
      setDownloadingIndex(index);
      const res = await dispatch(downloadLeadFile(filePath)).unwrap();
      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${fileName} downloaded`);
    } catch (err) {
      toast.error(err?.message || 'Download failed');
    } finally {
      setDownloadingIndex(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied!');
    } catch {
      toast.error('Copy failed');
    }
  };

  const setNewStatus = (status) =>
    setStatusData((p) => ({ ...p, newStatus: status }));
  const setSelectedUsers = (userIds) => {
    const ids = Array.isArray(userIds) ? userIds : [];
    setAssignData((p) => ({ ...p, selectedUsers: ids.slice(0, 1) }));
  };

  const handleConditionalChange = (field, value) => {
    setStatusData((p) => ({
      ...p,
      conditionalData: { ...p.conditionalData, [field]: value },
    }));
  };

  return {
    dialogs,
    statusData,
    assignData,
    assignUserOptions,
    assignUserOptionsLoading,
    shareData,
    shareLoading,
    attachments,
    attachmentsLoading,
    downloadingIndex,
    assignedUsers,
    assignedUsersLoading,
    activityLogs,
    activityLogsLoading,
    selectedLead,
    handleViewLead,
    handleEditLead,
    handleCreateLead,
    handleCreateDba,
    handleEditDba,
    handleOpenStatusDialog,
    handleCloseStatusDialog,
    handleOpenAssignDialog,
    handleCloseAssignDialog,
    handleOpenShareDialog,
    handleCloseShareDialog,
    handleOpenAttachmentsDialog,
    handleCloseAttachmentsDialog,
    handleOpenAssignedUsersDialog,
    handleCloseAssignedUsersDialog,
    handleOpenActivityLogsDialog,
    handleCloseActivityLogsDialog,
    handleStatusChange,
    handleAssign,
    handleDeleteLead,
    handleDownloadFile,
    copyToClipboard,
    setNewStatus,
    setSelectedUsers,
    handleConditionalChange,
    setSelectedLead,
    handleOpenIssueDialog,
    handleCloseIssueDialog,
    handleCreateIssue,
    handleOpenCreateCustomerDialog,
    handleCloseCreateCustomerDialog,
    handleCreateCustomer,
    showCredentialsDialog,
    setShowCredentialsDialog,
    createdCustomer,
    credentialsDialogMeta,
    handleCloseCredentialsDialog,
    handleRegenerateCustomerCredentials,
    handleOpenInitialDealDialog,
    handleCloseInitialDealDialog,
    cancelAttachmentTarget,
    handleOpenCancelAttachmentDialog,
    handleCloseCancelAttachmentDialog,
    handleCancelAttachment,
    addAttachmentTarget,
    handleOpenAddAttachmentDialog,
    handleCloseAddAttachmentDialog,
    handleOpenHoldIuwStatusDialog,
    handleOpenResolvedIuwStatusDialog,
  };
};
